/**
 * Blueprint Theme — Swiss/Architectural Precision
 *
 * Drafting-paper background, hairline rules, strict grid, near-sharp corners.
 * Single ink-blue accent, graphite monochrome, tabular numerals.
 * IBM Plex Sans for UI; IBM Plex Mono for data/numerals.
 */

import type { AppTheme } from './types';

const palette = {
  // Layout / background
  bg: {
    base: '#f8f9fa',      // drafting paper
    surface: '#ffffff',    // cards, panels
    elevated: '#ffffff',   // dropdowns, popovers
    hover: '#edf2f7',      // row hover
    active: '#f1f5f9',     // row alt / selected
  },
  text: {
    primary: '#0f172a',    // graphite ink
    secondary: '#475569',
    tertiary: '#64748b',
    muted: '#94a3b8',
  },
  border: {
    default: '#cbd5e1',
    subtle: '#dde3ec',
    strong: '#94a3b8',
  },
  accent: {
    primary: '#1e40af',    // ink blue
    light: '#dbeafe',
  },
  success: '#166534',
  successSubtle: '#f0fdf4',
  warning: '#b45309',
  warningSubtle: '#fffbeb',
  error: '#dc2626',
  errorSubtle: '#fef2f2',
  info: '#1e40af',
  infoSubtle: '#eff6ff',
};

export const blueprintTheme: AppTheme = {
  name: 'blueprint',
  displayName: 'Blueprint',
  description: 'Swiss/architectural precision — drafting-paper ground, ink-blue accent, hairline rules',

  antdTheme: {
    token: {
      colorPrimary: palette.accent.primary,
      colorInfo: palette.accent.primary,
      colorLink: palette.accent.primary,
      colorLinkHover: '#2563eb',
      colorLinkActive: '#1e3a8a',

      colorSuccess: palette.success,
      colorWarning: palette.warning,
      colorError: palette.error,

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      // Sharp/minimal corners — Blueprint is gridded and precise
      borderRadius: 2,
      borderRadiusLG: 3,
      borderRadiusSM: 2,
      borderRadiusXS: 1,

      colorBgContainer: palette.bg.surface,
      colorBgElevated: palette.bg.elevated,
      colorBgLayout: palette.bg.base,

      colorBorder: palette.border.default,
      colorBorderSecondary: palette.border.subtle,

      colorText: palette.text.primary,
      colorTextSecondary: palette.text.secondary,
      colorTextTertiary: palette.text.tertiary,
      colorTextQuaternary: palette.text.muted,

      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
      boxShadowSecondary: '0 1px 2px rgba(15, 23, 42, 0.04)',
    },

    components: {
      Layout: {
        headerBg: palette.bg.surface,
        headerColor: palette.text.primary,
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: palette.bg.surface,
        bodyBg: palette.bg.base,
      },

      Button: {
        borderRadius: 2,
        borderRadiusLG: 3,
        borderRadiusSM: 2,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 2,
        borderRadiusLG: 3,
        colorBgContainer: palette.bg.surface,
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: palette.bg.surface,
        headerColor: palette.text.tertiary,
        headerBorderRadius: 2,
        borderRadius: 2,
        rowHoverBg: palette.bg.hover,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        borderColor: palette.border.subtle,
        headerSplitColor: palette.border.subtle,
      },

      Input: {
        borderRadius: 2,
        controlHeight: 36,
        controlHeightLG: 42,
        paddingBlock: 8,
        paddingInline: 12,
        activeBorderColor: palette.accent.primary,
        hoverBorderColor: palette.border.strong,
        activeShadow: '0 0 0 2px rgba(30, 64, 175, 0.12)',
      },

      Select: {
        borderRadius: 2,
        controlHeight: 36,
        controlHeightLG: 42,
        optionSelectedBg: palette.accent.light,
        optionActiveBg: palette.bg.hover,
      },

      Tag: {
        borderRadiusSM: 2,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 3,
        boxShadow: '0 4px 16px rgba(15, 23, 42, 0.10)',
      },

      Drawer: {
        colorBgElevated: palette.bg.surface,
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: palette.text.muted,
        linkColor: palette.accent.primary,
        linkHoverColor: '#2563eb',
        separatorColor: palette.text.muted,
      },

      Segmented: {
        borderRadius: 2,
        itemSelectedBg: palette.accent.primary,
        itemSelectedColor: '#ffffff',
        itemColor: palette.text.secondary,
        trackBg: palette.bg.active,
      },

      Tabs: {
        inkBarColor: palette.accent.primary,
        itemColor: palette.text.tertiary,
        itemSelectedColor: palette.accent.primary,
        itemHoverColor: palette.text.primary,
        itemActiveColor: palette.accent.primary,
      },

      Dropdown: {
        borderRadiusLG: 3,
        boxShadowSecondary: '0 4px 16px rgba(15, 23, 42, 0.10)',
      },

      Tooltip: {
        borderRadius: 2,
      },

      Divider: {
        colorSplit: palette.border.subtle,
      },

      Alert: {
        colorInfoBg: palette.infoSubtle,
        colorSuccessBg: palette.successSubtle,
        colorWarningBg: palette.warningSubtle,
        colorErrorBg: palette.errorSubtle,
      },

      Popover: {
        colorBgElevated: palette.bg.elevated,
      },

      DatePicker: {
        colorBgContainer: palette.bg.surface,
        activeBorderColor: palette.accent.primary,
        hoverBorderColor: palette.border.strong,
      },

      Pagination: {
        itemBg: 'transparent',
        itemActiveBg: palette.accent.light,
      },

      Collapse: {
        headerBg: palette.bg.active,
        contentBg: palette.bg.surface,
      },

      Switch: {
        colorPrimary: palette.accent.primary,
        colorPrimaryHover: '#2563eb',
      },

      Progress: {
        remainingColor: palette.bg.active,
      },

      Skeleton: {
        color: palette.bg.active,
        colorGradientEnd: palette.bg.hover,
      },

      Empty: {
        colorTextDescription: palette.text.tertiary,
      },

      Spin: {
        colorPrimary: palette.accent.primary,
      },

      Timeline: {
        dotBg: palette.bg.surface,
      },
    },
  },

  cssVariables: {
    // Layout
    '--layout-header-bg': palette.bg.surface,
    '--layout-sidebar-bg': palette.bg.surface,
    '--layout-content-bg': palette.bg.base,

    // Surface elevation
    '--surface-0': palette.bg.base,
    '--surface-1': palette.bg.surface,
    '--surface-2': palette.bg.elevated,
    '--surface-3': palette.bg.elevated,

    // Text hierarchy
    '--text-primary': palette.text.primary,
    '--text-secondary': palette.text.secondary,
    '--text-tertiary': palette.text.tertiary,
    '--text-muted': palette.text.muted,

    // Border progression
    '--border-default': palette.border.default,
    '--border-subtle': palette.border.subtle,
    '--border-strong': palette.border.strong,

    // Legacy
    '--border-color': palette.border.default,
    '--border-radius': '2px',

    // Shadows
    '--shadow-sm': '0 1px 2px rgba(15, 23, 42, 0.05)',
    '--shadow-md': '0 1px 3px rgba(15, 23, 42, 0.07)',
    '--shadow-lg': '0 4px 16px rgba(15, 23, 42, 0.10)',

    // Accents
    '--accent-warm': palette.warning,
    '--accent-cool': palette.accent.primary,
    '--accent-ink': palette.accent.primary,

    // Status
    '--status-success': palette.success,
    '--status-warning': palette.warning,
    '--status-error': palette.error,
    '--status-info': palette.info,

    // Status subtle
    '--status-success-subtle': palette.successSubtle,
    '--status-warning-subtle': palette.warningSubtle,
    '--status-error-subtle': palette.errorSubtle,
    '--status-info-subtle': palette.infoSubtle,

    // Controls
    '--control-bg': palette.bg.base,
    '--control-border': palette.border.default,
    '--control-hover': palette.bg.hover,

    // Interactive
    '--hover-bg': palette.bg.hover,
    '--active-bg': palette.bg.active,

    // Blueprint-specific additions
    '--grid-line': '#dde3ec',
    '--font-mono': '"IBM Plex Mono", "SFMono-Regular", monospace',
  },
};
